<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('animales')">Animales</inertia-link>
      <span class="text-indigo-400 font-medium">/</span>
      Pesos
      <span class="text-gray-500 text-base font-normal ml-2">
        ({{ animal.arete }} - {{ animal.nombre }}) | Peso actual: {{ animal.peso_actual || '-' }} kg
      </span>
    </h1>

    <div class="mb-6 flex justify-between items-center">
      <search-filter v-model="form.search" class="w-full max-w-md mr-4" @reset="reset">
        <label class="block text-gray-700">Trashed:</label>
        <select v-model="form.trashed" class="mt-1 w-full form-select">
          <option :value="null" />
          <option value="with">With Trashed</option>
        </select>
      </search-filter>

      <div class="flex gap-2">
        <inertia-link class="btn-indigo" :href="route('animales.edit', animal.id)">
          <span>Volver</span>
          <span class="hidden md:inline">Animal</span>
        </inertia-link>

        <button class="btn-indigo" type="button" @click="openModal = true">
          <span>Registrar</span>
          <span class="hidden md:inline">Peso</span>
        </button>
      </div>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Fecha</th>
          <th class="px-6 pt-6 pb-4">Peso (kg)</th>
          <th class="px-6 pt-6 pb-4">Método</th>
          <th class="px-6 pt-6 pb-4">Observación</th>
        </tr>

        <tr v-for="p in pesos.data" :key="p.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t px-6 py-4">{{ p.fecha }}</td>
          <td class="border-t px-6 py-4">{{ p.peso }}</td>
          <td class="border-t px-6 py-4">{{ p.metodo || '-' }}</td>
          <td class="border-t px-6 py-4">{{ p.observacion || '-' }}</td>
        </tr>

        <tr v-if="pesos.data.length === 0">
          <td class="border-t px-6 py-4" colspan="4">No hay pesajes registrados</td>
        </tr>
      </table>
    </div>

    <pagination class="mt-6" :links="pesos.links" />

    <!-- MODAL PESO -->
    <div v-if="openModal" class="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        class="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/60"
        @click="closeModal"
      ></div>

      <div class="relative bg-white rounded-md shadow-xl overflow-hidden w-full max-w-3xl">
        <div class="px-8 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
          <div class="font-bold">Registrar Peso</div>
          <button type="button" class="text-gray-500 hover:text-gray-800" @click="closeModal">✕</button>
        </div>

        <form @submit.prevent="store">
          <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
            <text-input v-model="pesoForm.fecha" :error="pesoForm.errors.fecha"
              class="pr-6 pb-8 w-full lg:w-1/3" label="Fecha" type="date" required />

            <text-input v-model="pesoForm.peso" :error="pesoForm.errors.peso"
              class="pr-6 pb-8 w-full lg:w-1/3" label="Peso (kg)" type="number" required />

            <select-input v-model="pesoForm.metodo" :error="pesoForm.errors.metodo"
              class="pr-6 pb-8 w-full lg:w-1/3" label="Método">
              <option :value="null">Seleccione</option>
              <option value="bascula">Báscula</option>
              <option value="cinta">Cinta</option>
              <option value="estimado">Estimado</option>
            </select-input>

            <text-input v-model="pesoForm.observacion" :error="pesoForm.errors.observacion"
              class="pr-6 pb-8 w-full" label="Observación" />
          </div>

          <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center">
            <loading-button :loading="pesoForm.processing" class="btn-indigo" type="submit">
              Guardar Peso
            </loading-button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  metaInfo: { title: 'Pesos' },
  components: { Pagination, SearchFilter, TextInput, SelectInput, LoadingButton },
  layout: Layout,
  props: {
    animal: Object,
    filters: [Object, Array],
    pesos: Object,
  },
  data() {
    return {
      openModal: false,
      form: {
        search: this.filters && this.filters.search ? this.filters.search : null,
        trashed: this.filters && this.filters.trashed ? this.filters.trashed : null,
      },
      pesoForm: this.$inertia.form({
        fecha: null,
        peso: null,
        metodo: null,
        observacion: null,
      }),
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          this.route('animales.pesos', { animal: this.animal.id }),
          pickBy(this.form),
          { preserveState: true }
        )
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    closeModal() {
      this.openModal = false
      this.pesoForm.clearErrors()
    },
    store() {
      this.pesoForm.post(this.route('animales.pesos.store', { animal: this.animal.id }), {
        preserveScroll: true,
        onSuccess: () => {
          this.openModal = false
          this.pesoForm.reset()
        },
      })
    },
  },
}
</script>